import { useEffect, useState } from "react";
import api from "../api";
import DateIT from "../components/DateIT";

const EMPTY = { name: "", senders: [], subjects: [], exclude_senders: [], exclude_subjects: [], match_all: false, require_attachments: true, mark_handled: false, action: "assign", folder_id: "", project_id: "", also_client_yearly: false };

export default function AutomationSettings() {
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [senderInput, setSenderInput] = useState("");
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selCompany, setSelCompany] = useState("");
  const [selProject, setSelProject] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingDest, setEditingDest] = useState("");
  const [sinceDate, setSinceDate] = useState("2026-01-01");
  const [autoArchive, setAutoArchive] = useState(true);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveMsg, setArchiveMsg] = useState("");
  const [fornitori, setFornitori] = useState([]);
  const [fornBusy, setFornBusy] = useState("");
  const [fornMsg, setFornMsg] = useState("");

  const load = () => api.get("/automations").then((r) => setRules(r.data)).catch(() => {});
  const loadFornitori = () => api.get("/fatture-estere/fornitori-nuovi?giorni=120")
    .then((r) => setFornitori(r.data.fornitori || [])).catch(() => setFornitori([]));
  useEffect(() => {
    load();
    loadFornitori();
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/emails/auto-archive-config").then((r) => setAutoArchive(!!r.data.enabled)).catch(() => {});
  }, []);

  const riconosci = async (f) => {
    setFornBusy(f.dominio); setFornMsg("");
    try {
      const r = await api.post("/fatture-estere/fornitori-nuovi/aggiungi", { dominio: f.dominio });
      setFornMsg(`${f.dominio} aggiunto alla regola: ${r.data.abbinate} email messe in cartella.`);
      loadFornitori(); load();
    } catch (e) { setFornMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setFornBusy(""); }
  };
  const ignora = async (f) => {
    setFornBusy(f.dominio); setFornMsg("");
    try {
      await api.post("/fatture-estere/fornitori-nuovi/ignora", { dominio: f.dominio });
      setFornMsg(`${f.dominio} non verrà più segnalato.`);
      loadFornitori();
    } catch (e) { setFornMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setFornBusy(""); }
  };

  const toggleAutoArchive = async (val) => {
    setAutoArchive(val);
    try { await api.put("/emails/auto-archive-config", { enabled: val }); } catch (err) { console.error("Errore aggiornamento auto-archiviazione:", err); }
  };
  const runAutoArchive = async () => {
    setArchiveBusy(true); setArchiveMsg(`Archiviazione automatica email-cliente dal ${sinceDate}…`);
    try {
      const r = await api.post("/emails/auto-archive-run", { since: sinceDate });
      setArchiveMsg(`Fatto: analizzate ${r.data.scanned} email, archiviate ${r.data.archived} email-cliente con PDF.`);
    } catch (e) { setArchiveMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setArchiveBusy(false); }
  };

  const onCompany = (cid) => {
    setSelCompany(cid); setSelProject(""); setFolders([]); setForm((f) => ({ ...f, folder_id: "", project_id: "" }));
    if (cid) api.get(`/projects?company_id=${cid}`).then((r) => setProjects(r.data));
  };
  const onProject = (pid) => {
    setSelProject(pid); setForm((f) => ({ ...f, folder_id: "", project_id: pid }));
    if (pid) api.get(`/projects/${pid}`).then((r) => setFolders(r.data.folders));
  };

  const addSender = () => {
    const v = senderInput.trim();
    if (!v || form.senders.includes(v)) { setSenderInput(""); return; }
    setForm((f) => ({ ...f, senders: [...f.senders, v] }));
    setSenderInput("");
  };
  const removeSender = (s) => setForm((f) => ({ ...f, senders: f.senders.filter((x) => x !== s) }));

  const create = async () => {
    if (!form.name.trim()) { setMsg("Dai un nome alla regola."); return; }
    if (form.senders.length === 0) { setMsg("Aggiungi almeno un mittente."); return; }
    if (form.action === "monthly" ? !form.project_id : !form.folder_id) {
      setMsg(form.action === "monthly" ? "Scegli il progetto per le cartelle mensili." : "Scegli la cartella di destinazione."); return;
    }
    setBusy(true); setMsg("");
    try {
      if (editingId) {
        await api.put(`/automations/${editingId}`, form);
        setMsg("Regola aggiornata.");
      } else {
        await api.post("/automations", form);
        setMsg("Regola creata.");
      }
      cancelEdit();
      load();
    } catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({ name: r.name, senders: r.senders || [], subjects: r.subjects || [], exclude_senders: r.exclude_senders || [], exclude_subjects: r.exclude_subjects || [], match_all: !!r.match_all, require_attachments: !!r.require_attachments, mark_handled: !!r.mark_handled, action: r.action || "assign", folder_id: r.folder_id || "", project_id: r.project_id || "", also_client_yearly: !!r.also_client_yearly });
    setEditingDest(r.company_name ? `${r.company_name} › ${r.folder_name}` : "");
    setSenderInput(""); setSelCompany(""); setSelProject(""); setProjects([]); setFolders([]); setMsg("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => {
    setEditingId(null); setEditingDest(""); setForm(EMPTY); setSenderInput("");
    setSelCompany(""); setSelProject(""); setProjects([]); setFolders([]);
  };

  const toggle = async (r) => { await api.put(`/automations/${r.id}`, { enabled: !r.enabled }); load(); };
  const remove = async (id) => { if (!window.confirm("Eliminare questa regola?")) return; await api.delete(`/automations/${id}`); load(); };

  const runNow = async () => {
    setBusy(true); setMsg(`Analizzo le email dal ${sinceDate}…`);
    try { const r = await api.post("/emails/apply-automations", { since: sinceDate }); setMsg(`Fatto: analizzate ${r.data.scanned}, abbinate ${r.data.applied} email.`); load(); }
    catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const applyToExisting = async (r) => {
    if (!window.confirm(`Applicare «${r.name}» alle email già arrivate dal ${sinceDate}? Le email restano in Da gestire, vengono solo abbinate alla cartella.`)) return;
    setBusy(true); setMsg(`Applico «${r.name}» allo storico…`);
    try {
      const res = await api.post(`/automations/${r.id}/applica-esistenti?dal=${sinceDate}`);
      setMsg(`Fatto: analizzate ${res.data.analizzate} email, abbinate ${res.data.abbinate}.`);
      load();
    } catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="panel" data-testid="fornitori-nuovi-panel" style={{ borderLeft: "4px solid #b45309" }}>
        <div className="toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ margin: 0 }}>🔎 Fornitori esteri nuovi da riconoscere
            {fornitori.length > 0 && <span className="chip" style={{ background: "#b45309", marginLeft: 10 }} data-testid="fornitori-nuovi-count">{fornitori.length}</span>}
          </h3>
          <div className="spacer" />
          <button className="btn sm" onClick={loadFornitori} data-testid="fornitori-nuovi-reload">Ricontrolla</button>
        </div>
        <p className="muted" style={{ marginTop: 6 }}>Email con oggetto da fattura/ricevuta arrivate negli ultimi 120 giorni da <strong>domini esteri che la regola non conosce ancora</strong>. Ogni mattina ti arriva un avviso quando ne spunta uno nuovo.</p>
        {fornitori.length === 0 ? (
          <p className="muted" data-testid="fornitori-nuovi-empty">Nessun fornitore estero nuovo: la regola li riconosce tutti.</p>
        ) : (
          <table style={{ marginTop: 6 }}>
            <thead><tr><th>Fornitore</th><th>Email</th><th>Quante</th><th>Ultima</th><th></th></tr></thead>
            <tbody>
              {fornitori.map((f) => (
                <tr key={f.dominio} data-testid={`fornitore-nuovo-${f.dominio}`}>
                  <td><strong>{f.nome}</strong><div className="muted" style={{ fontSize: 12 }}>{(f.esempi || []).map((e) => e.oggetto).join(" · ")}</div></td>
                  <td className="muted">{f.mittente}</td>
                  <td>{f.quante}</td>
                  <td className="muted" style={{ whiteSpace: "nowrap" }}>{f.ultima ? new Date(f.ultima).toLocaleDateString("it-IT") : "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn sm primary" onClick={() => riconosci(f)} disabled={fornBusy === f.dominio} data-testid={`fornitore-add-${f.dominio}`}>✓ È un fornitore</button>{" "}
                    <button className="btn sm" onClick={() => ignora(f)} disabled={fornBusy === f.dominio} data-testid={`fornitore-ignore-${f.dominio}`}>Ignora</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {fornMsg && <p className="muted" data-testid="fornitori-nuovi-msg" style={{ marginTop: 6 }}>{fornMsg}</p>}
      </div>

      <div className="panel" data-testid="auto-archive-panel" style={{ marginTop: 18, borderLeft: "4px solid var(--green)" }}>
        <div className="toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ margin: 0 }}>📥 Archiviazione automatica email-cliente</h3>
          <div className="spacer" />
          <label className="muted" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={autoArchive} onChange={(e) => toggleAutoArchive(e.target.checked)} data-testid="auto-archive-toggle" />
            Attiva
          </label>
        </div>
        <p className="muted" style={{ marginTop: 6 }}>Quando arriva una mail da un <strong>cliente riconosciuto</strong> (azienda/persona in anagrafica) con allegati <strong>PDF</strong>, il CRM li salva automaticamente nella cartella <strong>"Bilancio {new Date().getFullYear()}"</strong> del cliente, ma <strong>la mail resta in «Da gestire»</strong> finché non la gestisci tu. Le regole di automazione specifiche hanno la precedenza.</p>
        <div className="toolbar" style={{ gap: 8 }}>
          <span className="muted">Applica allo storico dal</span>
          <DateIT style={{ maxWidth: 170 }} value={sinceDate} onChange={(v) => setSinceDate(v)} data-testid="auto-archive-since" />
          <button className="btn" onClick={runAutoArchive} disabled={archiveBusy} data-testid="auto-archive-run">Archivia ora le email-cliente</button>
        </div>
        {archiveMsg && <p className="muted" data-testid="auto-archive-msg" style={{ marginTop: 6 }}>{archiveMsg}</p>}
      </div>

      <div className="panel" style={{ marginTop: 18 }}>
        <h3>{editingId ? "Modifica automazione" : "Nuova automazione"}</h3>
        <p className="muted">Quando arriva una mail da uno dei mittenti indicati (e con allegati, se richiesto), il CRM salva i PDF nell'archivio della cartella scelta e segna la mail come <strong>gestita</strong>.</p>

        <div className="field"><label>Nome regola</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Fatture estere fornitori software" data-testid="rule-name" />
        </div>

        <div className="field"><label>Mittenti (nome o dominio, anche parziale)</label>
          <div className="toolbar" style={{ gap: 6 }}>
            <input style={{ maxWidth: 320 }} value={senderInput} onChange={(e) => setSenderInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSender())} placeholder="es. adobe, emergent, merenda, highlevel" data-testid="rule-sender-input" />
            <button type="button" className="btn sm" onClick={addSender} data-testid="rule-sender-add">+ Aggiungi mittente</button>
          </div>
          <div className="tag-filter" style={{ marginTop: 8 }} data-testid="rule-senders">
            {form.senders.map((s) => (
              <span key={s} className="chip" style={{ background: "#2563eb", cursor: "pointer" }} onClick={() => removeSender(s)} title="Rimuovi">{s} ✕</span>
            ))}
            {form.senders.length === 0 && <span className="muted">Nessun mittente aggiunto.</span>}
          </div>
        </div>

        <div className="field"><label>Parole nell'oggetto (facoltative: bastano queste <em>oppure</em> il mittente)</label>
          <input value={(form.subjects || []).join(", ")} onChange={(e) => setForm({ ...form, subjects: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
            placeholder="es. invoice, receipt, tax invoice, billing" data-testid="rule-subjects" />
        </div>

        <div className="field"><label>Mittenti da escludere (facoltativo)</label>
          <input value={(form.exclude_senders || []).join(", ")} onChange={(e) => setForm({ ...form, exclude_senders: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
            placeholder="es. .it, @pec. — per non pescare le fatture italiane" data-testid="rule-exclude" />
        </div>

        <div className="field"><label>Parole nell'oggetto da escludere (facoltativo)</label>
          <input value={(form.exclude_subjects || []).join(", ")} onChange={(e) => setForm({ ...form, exclude_subjects: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
            placeholder="es. richiesta, sollecito — così le domande non vengono archiviate" data-testid="rule-exclude-subjects" />
        </div>

        <label className="muted" style={{ display: "flex", gap: 8, alignItems: "center", margin: "8px 0" }}>
          <input type="checkbox" checked={!!form.match_all} onChange={(e) => setForm({ ...form, match_all: e.target.checked })} data-testid="rule-match-all" />
          Devono valere <strong>&nbsp;sia il mittente sia l'oggetto</strong>&nbsp;<span className="muted">(consigliato per le fatture: così non pesca avvisi e pubblicità degli stessi fornitori)</span>
        </label>

        <label className="muted" style={{ display: "flex", gap: 8, alignItems: "center", margin: "8px 0" }}>
          <input type="checkbox" checked={form.require_attachments} onChange={(e) => setForm({ ...form, require_attachments: e.target.checked })} data-testid="rule-require-att" /> Solo email <strong>&nbsp;con allegati</strong>&nbsp;<span className="muted">(toglilo per le ricevute estere, che hanno il PDF dietro un link)</span>
        </label>

        <label className="muted" style={{ display: "flex", gap: 8, alignItems: "center", margin: "8px 0" }}>
          <input type="checkbox" checked={!!form.mark_handled} onChange={(e) => setForm({ ...form, mark_handled: e.target.checked })} data-testid="rule-mark-handled" />
          Togli la mail da <strong>&nbsp;«Da gestire»</strong>&nbsp;appena viene abbinata&nbsp;<span className="muted">(per le fatture estere: le trovi direttamente nella cartella)</span>
        </label>

        <div className="field"><label>Tipo destinazione</label>
          <select value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value, folder_id: "", project_id: "" })} data-testid="rule-action">
            <option value="assign">Cartella fissa</option>
            <option value="monthly">Sottocartelle per mese (AAAA-MM)</option>
          </select>
        </div>

        <div className="grid-2">
          <div className="field"><label>Azienda / Studio</label>
            <select value={selCompany} onChange={(e) => onCompany(e.target.value)} data-testid="rule-company">
              <option value="">— seleziona —</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {selCompany && (
            <div className="field"><label>Progetto</label>
              <select value={selProject} onChange={(e) => onProject(e.target.value)} data-testid="rule-project">
                <option value="">— seleziona —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
        </div>
        {form.action === "assign" && selProject && (
          <div className="field"><label>Cartella di destinazione</label>
            <select value={form.folder_id} onChange={(e) => setForm({ ...form, folder_id: e.target.value })} data-testid="rule-folder">
              <option value="">— seleziona —</option>
              {folders.filter((f) => !f.closed).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
        )}
        {form.action === "monthly" && selProject && (
          <p className="muted" data-testid="rule-monthly-note" style={{ marginTop: 4 }}>I PDF verranno salvati in sottocartelle automatiche per mese di arrivo (es. <strong>2026-06 Giugno</strong>) dentro il progetto selezionato.</p>
        )}
        {form.action === "monthly" && (
          <label className="muted" style={{ display: "flex", gap: 8, alignItems: "center", margin: "8px 0" }}>
            <input type="checkbox" checked={form.also_client_yearly} onChange={(e) => setForm({ ...form, also_client_yearly: e.target.checked })} data-testid="rule-client-yearly" />
            Copia i PDF <strong>&nbsp;anche nella cartella "Bilancio {new Date().getFullYear()}" del cliente</strong>&nbsp;(cliente abbinato dalla ragione sociale nell'oggetto)
          </label>
        )}
        {editingId && editingDest && !selProject && (
          <p className="muted" data-testid="rule-current-dest" style={{ marginTop: 4 }}>Destinazione attuale: <strong>{editingDest}</strong>. Selezionala sopra solo se vuoi cambiarla.</p>
        )}

        <div className="toolbar" style={{ marginTop: 6 }}>
          <button className="btn primary" onClick={create} disabled={busy} data-testid="rule-create-btn">{editingId ? "Salva modifiche" : "Crea regola"}</button>
          {editingId && <button className="btn" onClick={cancelEdit} data-testid="rule-cancel-btn">Annulla</button>}
        </div>
        {msg && <p className="muted" data-testid="rule-msg" style={{ marginTop: 6 }}>{msg}</p>}
      </div>

      <div className="panel" style={{ marginTop: 18 }}>
        <div className="toolbar" style={{ flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ margin: 0 }}>Regole attive</h3>
          <div className="spacer" />
          <span className="muted">Analizza dal</span>
          <DateIT style={{ maxWidth: 170 }} value={sinceDate} onChange={(v) => setSinceDate(v)} data-testid="rule-since-date" />
          <button className="btn" onClick={runNow} disabled={busy} data-testid="rule-run-now">Applica regole alle email</button>
        </div>
        <table style={{ marginTop: 10 }}>
          <thead><tr><th>Nome</th><th>Mittenti</th><th>Destinazione</th><th>Abbinate</th><th>Attiva</th><th></th></tr></thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} data-testid={`rule-row-${r.id}`}>
                <td>{r.name}</td>
                <td>{(r.senders || []).join(", ") || "—"}{r.require_attachments && <div className="muted" style={{ fontSize: 11 }}>solo con allegati</div>}</td>
                <td>{r.company_name ? `${r.company_name} › ${r.folder_name}` : <span className="muted">cartella mancante</span>}</td>
                <td>{r.matched_count || 0}</td>
                <td><input type="checkbox" checked={!!r.enabled} onChange={() => toggle(r)} data-testid={`rule-toggle-${r.id}`} /></td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="btn sm" onClick={() => startEdit(r)} data-testid={`rule-edit-${r.id}`}>Modifica</button>{" "}
                  <button className="btn sm" onClick={() => applyToExisting(r)} disabled={busy} title="Applica questa regola alle email già arrivate" data-testid={`rule-apply-${r.id}`}>Applica allo storico</button>{" "}
                  <button className="btn sm danger" onClick={() => remove(r.id)} data-testid={`rule-delete-${r.id}`}>Elimina</button>
                </td>
              </tr>
            ))}
            {rules.length === 0 && <tr><td colSpan={6}><div className="empty">Nessuna automazione.</div></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
